// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, CommonModule, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import defaultLanguage from '../../../../../assets/i18n/en.json';
import { ScreenLayoutService } from '../../../shared/services/screen-layout-service/screen-layout.service';
import { SettingsService } from '../../../shared/services/settings.service';
import { FeatureDetailsService } from '../../services/feature-details.service';
import { RouteService } from '../../services/routeService.service';
import { SystemOverviewService } from '../../services/systemOverview.service';
import { UserService } from '../../services/user.service';
import { AuthorizedComponent } from '../authorized/authorized.component';
import { FeatureDetailsComponent } from '../feature-details/feature-details.component';
import { FooterComponent } from '../footer/footer.component';

import { LocalizedDatePipe } from '../../../shared/pipes/localizedDate.pipe';
import { AnnouncementService } from '../../services/announcement-service';
import { ReleaseNotesComponent } from '../release-notes/release-notes.component';
import { SearchPaneComponent } from '../search-pane/search-pane.component';
import { SettingsComponent } from '../settings/settings.component';
import { UsefulApplications } from '../useful-applications/useful-applications';
import { UserDocumentationComponent } from '../user-documentation/user-documentation.component';
import { map, Observable, take } from 'rxjs';
import { AuthService } from '../../services/authorize-service.service';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgTemplateOutlet,
    UserDocumentationComponent,
    SettingsComponent,
    ReleaseNotesComponent,
    UsefulApplications,
    AuthorizedComponent,
    NgClass,
    RouterOutlet,
    SearchPaneComponent,
    FooterComponent,
    AsyncPipe,
    TranslateModule,
    FeatureDetailsComponent,
    LocalizedDatePipe,
    CommonModule
  ],
})
export class AppComponent implements OnInit {
  private translate = inject(TranslateService);
  userService = inject(UserService);
  settingsController = inject(SettingsService);
  routeService = inject(RouteService);
  systemOverviewService = inject(SystemOverviewService);
  router = inject(Router);
  screenLayoutService = inject(ScreenLayoutService);
  protected announcementService = inject(AnnouncementService);
  private featureDetailsService = inject(FeatureDetailsService);
  isAuthorized$!: Observable<boolean>;
  isAuthorized = false;

  @ViewChild('accessbar') accessBar?: ElementRef<HTMLElement>;
  @ViewChild('drawerMenu') drawerMenu!: ElementRef<HTMLInputElement>;
  @ViewChild('drawerApps') drawerApps!: ElementRef<HTMLInputElement>;

  title = 'SeSO Portal';
  isWhatsNewEnabled = false;
  searchMessage?: {
    message: string;
    icon: string;
  };
  constructor(private readonly authService: AuthService) {
    this.setTranslation();
  }

  ngOnInit(): void {
    this.featureDetailsService.whatsNewConfig.lastSuccessfulData$.subscribe(
      (data) => (this.isWhatsNewEnabled = data.isWhatsNewEnabled ?? false),
    );
    this.isAuthorized$ = this.authService.checkUserAuthorization().pipe(
      map((status) => status === true)
    );
    this.isAuthorized$.pipe(take(1)).subscribe((val) => (this.isAuthorized = val));
  
  }

  private setTranslation() {
    this.translate.setTranslation('en', defaultLanguage);
    this.translate.setDefaultLang('en');
  }

  openMenu(): void {
    this.drawerMenu.nativeElement.setAttribute('visible', '');
  }

  closeMenu(): void {
    this.drawerMenu.nativeElement.removeAttribute('visible');
  }

  openApps(): void {
    this.drawerApps.nativeElement.setAttribute('visible', '');
  }

  closeApps(): void {
    this.drawerApps.nativeElement.removeAttribute('visible');
  }

  openSearch(): void {
    this.routeService.openSearch(false);
  }

  cancelSearch(): void {
    this.routeService.setSearchString('');
  }

  goHome(): void {
    this.closeMenu();
    const target = this.isAuthorized ? '/' : '/unauthorized';
    this.router.navigate([target]);
  }

  goToSettings(): void {
    this.closeMenu();
    this.router.navigate(['/settings']);
  }

  getHelp(): void {
    this.closeMenu();
    window.open(
      'https://web.yammer.com/main/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiI0MTgyOTEzMDI0MCJ9',
      '_blank',
      'noopener',
    );
  }

  isLandingPage(): boolean {
    return this.router.url === '/';
  }

  handleEventClick(e: Event): void {
    e.stopPropagation();
    const target = e.composedPath()[0] as HTMLElement;
    if (target.classList.contains('menu-item-label')) {
      (e.target as any).expanded = !(e.target as any).expanded;
    }
  }

  matchLocation(m: string): boolean {
    return !!this.router.url.match(new RegExp(m));
  }

  showWhatsNewModal(): void {
    this.closeMenu();
    this.featureDetailsService.showFeatureDetailsModal.next(true);
  }

  routeToReleaseNotes(): void {
    this.closeMenu();
    this.router.navigateByUrl('/releaseNotes');
  }

  routeToAdvancedSearch(): void {
    this.closeMenu();
    this.router.navigateByUrl('/advancedSearch');
  }
}
